import { useEffect, useRef, useState } from 'react';

import { Markdown } from '@/ui/Markdown';
import { Modal } from '@/ui/Modal';

function TextTileDialog({ label, text, markdown, onClose }: { label: string; text: string; markdown: boolean; onClose: () => void }) {
  return (
    <Modal
      title={label}
      size={markdown ? 'wide' : undefined}
      onClose={onClose}
      footer={<button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>}
    >
      {markdown ? <Markdown>{text}</Markdown> : <p className="idea-tile-full">{text}</p>}
    </Modal>
  );
}

/** Whether the element's content overflows its clamped height, kept up to date as it resizes. */
function useClamped(text: string | null) {
  const body = useRef<HTMLDivElement>(null);
  const [clamped, setClamped] = useState(false);
  useEffect(() => {
    const el = body.current;
    if (!el) return;
    const measure = () => setClamped(el.scrollHeight > el.clientHeight + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text]);
  return { body, clamped };
}

/**
 * One of the model's texts in a tile of its own: the summary of the
 * opportunity, the rationale behind the verdict (plain text), or the
 * recommendation (Markdown, often a numbered list of next steps). Clamped so
 * the strip keeps its size; when the text is cut, See more opens it whole.
 */
export function TextTile({ label, text, className, markdown = false }: { label: string; text: string | null; className: string; markdown?: boolean }) {
  const { body, clamped } = useClamped(text);
  const [open, setOpen] = useState(false);

  return (
    <section className={`metric-tile usage-card idea-tile-text ${className}`} aria-label={label}>
      <span className="usage-card-of">{label}</span>
      {text ? (
        <div ref={body} className={markdown ? 'idea-tile-markdown' : 'idea-tile-prose'}>
          {markdown ? <Markdown>{text}</Markdown> : text}
        </div>
      ) : (
        <p className="hint">Nothing was written.</p>
      )}
      {clamped && (
        <button type="button" className="idea-tile-more" onClick={() => setOpen(true)}>
          See more
        </button>
      )}
      {open && text && <TextTileDialog label={label} text={text} markdown={markdown} onClose={() => setOpen(false)} />}
    </section>
  );
}
